import { useEffect, useState } from "react";
import { getProctoringReport } from "../../../../services/proctoringService";
import { 
  ShieldAlert, 
  ShieldCheck, 
  CheckCircle, 
  AlertTriangle, 
  Clock, 
  Camera, 
  ExternalLink,
  ChevronDown,
  ChevronUp,
  UserCheck,
  UserX
} from "lucide-react";

const ProctoringReportSection = ({ sessionId }) => {
  const [proctoringReport, setProctoringReport] = useState(null);
  const [loadingProctoring, setLoadingProctoring] = useState(false);
  const [errorProctoring, setErrorProctoring] = useState(null);
  const [isProctoringOpen, setIsProctoringOpen] = useState(false);
  const [activeSnapshot, setActiveSnapshot] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (!sessionId) return;

    const fetchProctoring = async () => {
      try {
        setLoadingProctoring(true);
        setErrorProctoring(null);
        const data = await getProctoringReport(sessionId);
        setProctoringReport(data);
        setHasAccess(true);
      } catch (err) {
        console.log("Proctoring report access denied (student/unauthorized):", err);
        setHasAccess(false);
      } finally {
        setLoadingProctoring(false);
      }
    };

    fetchProctoring();
  }, [sessionId]);

  if (!hasAccess || !proctoringReport) return null;

  return (
    <div className="mb-8 border border-gray-300 rounded-xl overflow-hidden bg-white shadow-sm">
      {/* Header (Clickable to Toggle) */}
      <button 
        onClick={() => setIsProctoringOpen(!isProctoringOpen)}
        className="w-full flex items-center justify-between p-5 bg-gray-50 border-b border-gray-200 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${proctoringReport?.violation_summary?.total_violations > 0 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
            {proctoringReport?.violation_summary?.total_violations > 0 ? (
              <ShieldAlert className="w-6 h-6" />
            ) : (
              <ShieldCheck className="w-6 h-6" />
            )}
          </div>
          <div className="text-left">
            <h2 className="text-lg font-semibold text-gray-800">Quiz Proctoring Integrity Report</h2>
            <p className="text-sm text-gray-500">
              {proctoringReport ? (
                `${proctoringReport.violation_summary.total_violations} violations flagged`
              ) : (
                'Analyzing candidate session integrity...'
              )}
            </p>
          </div>
        </div>
        <div>
          {isProctoringOpen ? (
            <ChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </div>
      </button>

      {/* Content */}
      {isProctoringOpen && (
        <div className="p-6 space-y-6">
          {loadingProctoring && (
            <div className="flex justify-center py-6">
              <div className="loading loading-spinner loading-md text-primary"></div>
            </div>
          )}

          {errorProctoring && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {errorProctoring}
            </div>
          )}

          {proctoringReport && (
            <>
              {/* Summary Metrics */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Overall status */}
                <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 flex flex-col justify-between">
                  <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Integrity Status</span>
                  <div className="mt-2 flex items-center gap-2">
                    {proctoringReport.violation_summary.total_violations > 5 ? (
                      <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold flex items-center gap-1.5">
                        <AlertTriangle className="w-4 h-4 text-red-600" /> Highly Suspicious
                      </span>
                    ) : proctoringReport.violation_summary.total_violations > 0 ? (
                      <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold flex items-center gap-1.5">
                        <AlertTriangle className="w-4 h-4 text-amber-600" /> Flagged Violations
                      </span>
                    ) : (
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold flex items-center gap-1.5">
                        <CheckCircle className="w-4 h-4 text-green-600" /> Integrity Confirmed
                      </span>
                    )}
                  </div>
                </div>

                {/* Total Violations */}
                <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 flex flex-col justify-between">
                  <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Flagged Events</span>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-2xl font-bold">{proctoringReport.violation_summary.total_violations}</span>
                    <span className="text-xs text-gray-500">logged occurrences</span>
                  </div>
                </div>

                {/* Identity Matches */}
                <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 flex flex-col justify-between">
                  <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Identity Verification</span>
                  <div className="mt-2">
                    {proctoringReport.snapshots?.length > 0 ? (
                      <div className="flex items-center gap-2">
                        {proctoringReport.snapshots.every(s => s.is_verified) ? (
                          <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold flex items-center gap-1.5">
                            <UserCheck className="w-4 h-4 text-green-600" /> Verified Match
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold flex items-center gap-1.5">
                            <UserX className="w-4 h-4 text-red-600" /> Match Failed
                          </span>
                        )}
                        <span className="text-xs text-gray-500">({proctoringReport.snapshots.length} checks)</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500 italic">No snapshots logged</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Violation Type Breakdown */}
              {proctoringReport.violation_summary.total_violations > 0 && (
                <div className="p-4 rounded-xl border border-gray-200 bg-white">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Violation Breakdown</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {Object.entries(proctoringReport.violation_summary.breakdown || {}).map(([key, count]) => {
                      if (count === 0) return null;
                      const formattedKey = key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                      return (
                        <div key={key} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                          <span className="block text-xs text-gray-500 truncate">{formattedKey}</span>
                          <span className="text-lg font-bold text-gray-800">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Detailed Violations Timeline */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-500" /> Detailed Violation Log
                </h3>
                {proctoringReport.violations?.length > 0 ? (
                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                    {proctoringReport.violations.map((violation) => (
                      <div key={violation.id} className="p-4 rounded-xl border border-red-100 bg-red-50/30 flex flex-col md:flex-row justify-between gap-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-semibold rounded-md">
                              {violation.violation_type_display}
                            </span>
                            <span className="text-xs text-gray-400">
                              {new Date(violation.timestamp).toLocaleDateString()} {new Date(violation.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-800">{violation.details || "No violation details provided."}</p>
                        </div>
                        {violation.snapshot_detail?.snapshot_image && (
                          <div className="relative group self-start md:self-center">
                            <img 
                              src={violation.snapshot_detail.snapshot_image} 
                              alt="Violation snapshot" 
                              className="h-16 w-24 object-cover rounded-lg border border-gray-300 cursor-zoom-in transition-transform hover:scale-105 duration-200"
                              onClick={() => setActiveSnapshot({
                                url: violation.snapshot_detail.snapshot_image,
                                title: violation.violation_type_display,
                                timestamp: violation.timestamp
                              })}
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white pointer-events-none rounded-lg transition-opacity duration-200">
                              <ExternalLink className="w-4 h-4" />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 border border-dashed border-gray-200 rounded-xl text-center">
                    <span className="text-3xl">🛡️</span>
                    <p className="text-gray-500 mt-2 text-sm">No suspicious candidate behaviour detected during this session.</p>
                  </div>
                )}
              </div>

              {/* Periodic Identity Verification Snapshots */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Camera className="w-4 h-4 text-gray-500" /> Periodic Verification Snapshots
                </h3>
                {proctoringReport.snapshots?.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-4">
                    {proctoringReport.snapshots.map((snap) => (
                      <div 
                        key={snap.id} 
                        className={`p-2 rounded-xl border flex flex-col items-center text-center cursor-pointer transition-all hover:shadow-md ${snap.is_verified ? 'border-green-200 bg-green-50/20' : 'border-red-200 bg-red-50/20'}`}
                        onClick={() => setActiveSnapshot({
                          url: snap.snapshot_image,
                          title: snap.is_verified ? "Identity Verified" : "Identity Verification Failed",
                          extra: `Similarity Score: ${snap.similarity_score}%`,
                          timestamp: snap.timestamp
                        })}
                      >
                        <img 
                          src={snap.snapshot_image} 
                          alt="Verification Snapshot" 
                          className="w-full aspect-[4/3] object-cover rounded-lg border border-gray-200"
                        />
                        <div className="mt-2 space-y-0.5">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${snap.is_verified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {snap.is_verified ? 'Matched' : 'Unmatched'}
                          </span>
                          <span className="block text-[10px] text-gray-500 font-mono">
                            {snap.similarity_score}% similarity
                          </span>
                          <span className="block text-[9px] text-gray-400">
                            {new Date(snap.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 border border-dashed border-gray-200 rounded-xl text-center">
                    <span className="text-3xl">📷</span>
                    <p className="text-gray-500 mt-2 text-sm">No verification snapshots were captured in this session.</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Snapshot Image Preview Modal */}
      {activeSnapshot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75">
          <div className="relative max-w-3xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="font-bold text-gray-800">{activeSnapshot.title}</h3>
                <p className="text-xs text-gray-500">
                  {new Date(activeSnapshot.timestamp).toLocaleString()} {activeSnapshot.extra ? `| ${activeSnapshot.extra}` : ''}
                </p>
              </div>
              <button 
                onClick={() => setActiveSnapshot(null)}
                className="btn btn-sm btn-circle btn-ghost"
              >
                ✕
              </button>
            </div>
            {/* Modal Image */}
            <div className="flex justify-center bg-gray-900 p-4">
              <img 
                src={activeSnapshot.url} 
                alt="Enlarged snapshot" 
                className="max-h-[70vh] object-contain rounded-lg shadow-md"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProctoringReportSection;
